import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '#environments';
import { inject, Injectable } from '@angular/core';
import { Dtos } from '#models';

@Injectable({
  providedIn: 'root',
})
export class PSSRService {
  private http = inject(HttpClient);

  public getDetails(pssrId: number): Observable<Dtos.PSSR> {
    return this.http.get<Dtos.PSSR>(`${environment.apiUrl}/pssr/${pssrId}`);
  }

  public create(pssr: Dtos.PSSR): Observable<Dtos.PSSR> {
    return this.http.post<Dtos.PSSR>(`${environment.apiUrl}/pssr`, pssr);
  }

  public createMulti(pssr: Dtos.PSSR[]): Observable<Dtos.PSSR[]> {
    return this.http.post<Dtos.PSSR[]>(`${environment.apiUrl}/pssr/createMulti`, pssr);
  }

  public createMultiWithParticipant(pssr: Dtos.PSSR[]): Observable<Dtos.PSSR[]> {
    return this.http.post<Dtos.PSSR[]>(`${environment.apiUrl}/pssr/createMultiWithParticipants`, pssr);
  }

  public update(pssr: Dtos.PSSR): Observable<Dtos.PSSR> {
    return this.http.put<Dtos.PSSR>(`${environment.apiUrl}/pssr`, pssr);
  }

  public currentUserPSSRs(filter: Dtos.PSSRFilter): Observable<Dtos.PSSR[]> {
    return this.http.post<Dtos.PSSR[]>(`${environment.apiUrl}/pssr/currentPSSR`, filter);
  }

  getPagedPSSRs(filter: Dtos.PSSRFilter, pagination: Dtos.PaginationRequest): Observable<Dtos.PagedList<Dtos.PSSR>> {
    const requestParams = {
      Filter: filter,
      Pagination: pagination,
    };
    return this.http.post<Dtos.PagedList<Dtos.PSSR>>(`${environment.apiUrl}/pssr/paged-list`, requestParams);
  }

  public getPSSRsByName(buId: number, name: string): Observable<Dtos.IdName[]> {
    return this.http.get<Dtos.IdName[]>(`${environment.apiUrl}/pssr/by-name/${buId}/${name}/`);
  }

  public pssrsByProjId(projId: number): Observable<Dtos.PSSR[]> {
    return this.http.get<Dtos.PSSR[]>(`${environment.apiUrl}/pssr/projectPSSRs/${projId}`);
  }

  public addParticipant(pssrUsers: Dtos.PSSRUser[]): Observable<Dtos.PSSRUserList> {
    return this.http.post<Dtos.PSSRUserList>(`${environment.apiUrl}/pssr/addParticipant`, pssrUsers);
  }

  public updateParticipant(pssrUser: Dtos.PSSRUser): Observable<Dtos.PSSRUser> {
    return this.http.post<Dtos.PSSRUser>(`${environment.apiUrl}/pssr/updateParticipant`, pssrUser);
  }

  public removeParticipant(id: number): Observable<Dtos.PSSRUser> {
    return this.http.put<Dtos.PSSRUser>(`${environment.apiUrl}/pssr/removeParticipant/${id}`, null);
  }

  public changeStatus(pssrId: number, statusId: number): Observable<Dtos.PSSR> {
    return this.http.put<Dtos.PSSR>(`${environment.apiUrl}/pssr/changeStatus/${pssrId}/${statusId}`, {
      updatedDate: new Date().toISOString(),
    });
  }

  public reOpenPSSR(pssrId: number) {
    return this.http.put<Dtos.PSSR>(`${environment.apiUrl}/pssr/reopen/${pssrId}`, null);
  }

  public deletePssr(pssrId: number) {
    return this.http.put<Dtos.PSSR>(`${environment.apiUrl}/pssr/delete/${pssrId}`, null);
  }

  public restorePssr(pssrId: number) {
    return this.http.put<Dtos.PSSR>(`${environment.apiUrl}/pssr/restore/${pssrId}`, null);
  }

  public updateTaskPlan(pssrId: number, taskPlanId: number) {
    return this.http.put(`${environment.apiUrl}/pssr/updateTaskPlan/${pssrId}/${taskPlanId}`, null);
  }

  public labelClass(status: number): string {
    if (status in Dtos.PSSRStatusCSSClasses) {
      return Dtos.PSSRStatusCSSClasses[status];
    }

    return '';
  }

  public getSectionUsers(pssrId: number, sectionId: number, sectionDesc: string): Observable<Dtos.SectionUser[]> {
    const id = sectionId > 0 ? sectionId : 0;

    return this.http.get<Dtos.SectionUser[]>(`${environment.apiUrl}/pssr/getPssrSectionUsers/${pssrId}/${id}`, {
      params: {
        sectionDesc,
      },
    });
  }

  public addSectionUser(sectionUser: Partial<Dtos.SectionUser>): Observable<Dtos.SectionUser> {
    return this.http.post<Dtos.SectionUser>(`${environment.apiUrl}/pssr/addSectionUser`, sectionUser);
  }

  public removeSectionUser(id: number) {
    return this.http.put(`${environment.apiUrl}/pssr/removeSectionUser/${id}`, null);
  }

  public download(pssrID: number) {
    return this.http.get(`${environment.apiUrl}/report/pdf/${pssrID}`, {
      responseType: 'blob',
    });
  }

  public sendEmailReminderWithFreeText(pssrID: number, textForReminder = '') {
    return this.http.post(`${environment.apiUrl}/pssr/${pssrID}/email`, {
      AdditionalTextForEmail: textForReminder,
    });
  }

  public sendEmailReminder(pssrID: number) {
    return this.http.get(`${environment.apiUrl}/pssr/sendEmail/${pssrID}`);
  }

  public sendEmailReminderJob2() {
    return this.http.get(`${environment.apiUrl}/pssr/sendEmailJob2`);
  }

  public uploadFile(file: Dtos.PSSRFile): Observable<Dtos.PSSRFile[]> {
    return this.http.post<Dtos.PSSRFile[]>(`${environment.apiUrl}/pssr/upload-file`, file);
  }

  public uploadToMOC(pssrId: number) {
    return this.http.post(`${environment.apiUrl}/pssr/upload-to-moc/${pssrId}`, {});
  }

  public getPSSRFiles(pssrId: number): Observable<Dtos.PSSRFile[]> {
    return this.http.get<Dtos.PSSRFile[]>(`${environment.apiUrl}/pssr/getFiles/${pssrId}`);
  }

  public removeFileByFileId(fileId: number): Observable<Dtos.ResultModel> {
    return this.http.delete<Dtos.ResultModel>(`${environment.apiUrl}/pssr/deleteFile/${fileId}`);
  }

  public getFileByGUID(pssrId: number, fileGUID: string): Observable<Dtos.PSSRFile> {
    return this.http.get<Dtos.PSSRFile>(`${environment.apiUrl}/pssr/${pssrId}/getFile2/${fileGUID}`);
  }

  public downloadPSSRFileById(pssrId: number, fileId: number): Observable<Dtos.PSSRFile> {
    return this.http.get<Dtos.PSSRFile>(`${environment.apiUrl}/pssr/${pssrId}/getFile/${fileId}`);
  }

  public downloadPSSRFileByGuid(pssrId: number, guid: string): Observable<Dtos.PSSRFile> {
    return this.http.get<Dtos.PSSRFile>(`${environment.apiUrl}/pssr/${pssrId}/getFile`, { params: { guid } });
  }
}
